import React, { createContext, useContext, useEffect, useState } from 'react';
import { cn } from '../../utils/cn';

// Consolidated Design System Context
const DesignSystemContext = createContext();

// Consolidated Design Tokens (merged from multiple sources)
const baseTokens = {
  // Brand Colors (Primary)
  color: {
    brand: {
      50: '239 246 255',
      100: '219 234 254',
      200: '191 219 254',
      300: '147 197 253',
      400: '96 165 250',
      500: '59 130 246',
      600: '37 99 235',
      700: '29 78 216',
      800: '30 64 175',
      900: '30 58 138',
      950: '23 37 84'
    },
    // Semantic Colors
    semantic: {
      success: '34 197 94',
      warning: '251 146 60',
      error: '239 68 68',
      info: '59 130 246'
    },
    // Neutral Colors
    neutral: {
      50: '250 250 250',
      100: '244 244 245',
      200: '228 228 231',
      300: '212 212 216',
      400: '161 161 170',
      500: '113 113 122',
      600: '82 82 91',
      700: '63 63 70',
      800: '39 39 42',
      900: '24 24 27',
      950: '9 9 11'
    }
  },

  // Typography System
  typography: {
    fontFamily: {
      display: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'],
      body: ['Inter', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'],
      mono: ['JetBrains Mono', 'Menlo', 'Monaco', 'monospace']
    },
    fontSize: {
      xs: ['0.75rem', { lineHeight: '1rem' }],
      sm: ['0.875rem', { lineHeight: '1.25rem' }],
      base: ['1rem', { lineHeight: '1.5rem' }],
      lg: ['1.125rem', { lineHeight: '1.75rem' }],
      xl: ['1.25rem', { lineHeight: '1.75rem' }],
      '2xl': ['1.5rem', { lineHeight: '2rem' }],
      '3xl': ['1.875rem', { lineHeight: '2.25rem' }],
      '4xl': ['2.25rem', { lineHeight: '2.5rem' }],
      '5xl': ['3rem', { lineHeight: '1' }],
      '6xl': ['3.75rem', { lineHeight: '1' }],
      '7xl': ['4.5rem', { lineHeight: '1' }],
      '8xl': ['6rem', { lineHeight: '1' }],
      '9xl': ['8rem', { lineHeight: '1' }]
    },
    fontWeight: {
      thin: '100',
      extralight: '200',
      light: '300',
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
      black: '900'
    }
  },

  // Spacing System (Modular Scale)
  spacing: {
    px: '1px',
    0: '0',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    11: '2.75rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem'
  },

  // Component Sizes
  sizing: {
    button: {
      sm: '2rem',
      md: '2.5rem',
      lg: '3rem'
    },
    input: {
      sm: '2rem',
      md: '2.5rem',
      lg: '3rem'
    },
    container: {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      '2xl': '1536px'
    }
  },

  // Shadows & Elevation
  shadow: {
    xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    sm: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
  },

  // Border Radius
  //
  // These are applied as INLINE styles by button/card/input via getToken(), so
  // they beat every stylesheet rule. That is why this scale previously kept the
  // whole app at 6px buttons and 8px cards even after the CSS tokens were
  // sharpened: --radius-md said 2px, and this object silently overrode it.
  //
  // They now defer to the CSS custom properties so there is one source of
  // truth. The literal after the comma is only a fallback for the case where
  // the stylesheet has not loaded. If you change a radius, change it in
  // src/styles/design-tokens.css and design-system.css, not here.
  borderRadius: {
    none: '0',
    xs: 'var(--radius-xs, 0)',
    sm: 'var(--radius-sm, 0.125rem)',
    md: 'var(--radius-md, 0.125rem)',
    lg: 'var(--radius-lg, 0.25rem)',
    xl: 'var(--radius-xl, 0.375rem)',
    '2xl': 'var(--radius-2xl, 0.5rem)',
    '3xl': 'var(--radius-3xl, 0.75rem)',
    full: 'var(--radius-full, 9999px)'
  },

  // Animation System
  animation: {
    duration: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
      slower: '500ms'
    },
    easing: {
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    }
  }
};

// Cultural Adaptation Tokens
const culturalTokens = {
  japanese: {
    ...baseTokens,
    color: {
      ...baseTokens.color,
      brand: {
        ...baseTokens.color.brand,
        // Japanese aesthetic adjustments
        500: '220 38 38', // Deep red (aka)
        600: '185 28 28'  // Darker red for cultural resonance
      }
    },
    typography: {
      ...baseTokens.typography,
      fontFamily: {
        ...baseTokens.typography.fontFamily,
        body: ['Noto Sans CJK JP', 'Inter', 'sans-serif'],
        display: ['Noto Serif CJK JP', 'serif']
      },
      lineHeight: {
        tighter: '1.25',
        tight: '1.375',
        normal: '1.5',
        relaxed: '1.625',
        loose: '2' // Japanese preference for more line height
      }
    }
  },

  swedish: {
    ...baseTokens,
    color: {
      ...baseTokens.color,
      brand: {
        ...baseTokens.color.brand,
        // Swedish blue and yellow
        500: '0 83 156', // Swedish blue
        400: '0 123 255', // Lighter blue
        300: '254 221 0'  // Swedish yellow accent
      }
    },
    spacing: {
      ...baseTokens.spacing,
      // Lagom - balanced spacing
      4: '1.125rem', // Slightly more generous
      8: '2.25rem'
    }
  },

  swiss: {
    ...baseTokens,
    color: {
      ...baseTokens.color,
      brand: {
        ...baseTokens.color.brand,
        // Swiss red
        500: '255 0 0',
        600: '220 20 20'
      }
    },
    typography: {
      ...baseTokens.typography,
      fontFamily: {
        ...baseTokens.typography.fontFamily,
        body: ['Helvetica Neue', 'Inter', 'sans-serif'] // Swiss precision
      }
    }
  }
};

// Component Standardization API
const componentAPI = {
  button: {
    variants: ['default', 'primary', 'secondary', 'outline', 'ghost', 'destructive'],
    sizes: ['sm', 'md', 'lg'],
    states: ['default', 'hover', 'active', 'focus', 'disabled', 'loading']
  },
  input: {
    variants: ['default', 'outlined', 'filled', 'underlined'],
    sizes: ['sm', 'md', 'lg'],
    states: ['default', 'focus', 'error', 'disabled']
  },
  card: {
    variants: ['default', 'elevated', 'outlined', 'filled'],
    sizes: ['sm', 'md', 'lg', 'xl']
  }
};

export const DesignSystemProvider = ({
  children,
  theme = 'light',
  culture = 'default',
  reducedMotion = false,
  highContrast = false
}) => {
  const [tokens, setTokens] = useState(baseTokens);
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [currentCulture, setCurrentCulture] = useState(culture);

  // Apply cultural tokens
  useEffect(() => {
    if (culture !== 'default' && culturalTokens[culture]) {
      setTokens(culturalTokens[culture]);
    } else {
      setTokens(baseTokens);
    }
  }, [culture]);

  // Apply CSS custom properties
  useEffect(() => {
    const root = document.documentElement;

    // Apply tokens as CSS variables
    const applyTokens = (tokenObj, prefix = '') => {
      Object.entries(tokenObj).forEach(([key, value]) => {
        const cssVar = `--${prefix}${key}`;

        if (typeof value === 'object' && !Array.isArray(value)) {
          applyTokens(value, `${prefix}${key}-`);
        } else {
          root.style.setProperty(cssVar, value);
        }
      });
    };

    applyTokens(tokens);

    // Apply accessibility preferences
    if (reducedMotion) {
      root.style.setProperty('--animation-duration-fast', '0ms');
      root.style.setProperty('--animation-duration-normal', '0ms');
      root.style.setProperty('--animation-duration-slow', '0ms');
    }

    if (highContrast) {
      root.style.setProperty('--color-contrast-ratio', '7:1');
    }
  }, [tokens, reducedMotion, highContrast]);

  const value = {
    tokens,
    currentTheme,
    currentCulture,
    setCurrentTheme,
    setCurrentCulture,
    componentAPI,
    cn,
    // Utility functions
    getToken: (path) => {
      return path.split('.').reduce((obj, key) => obj?.[key], tokens);
    },
    setCustomToken: (path, value) => {
      const newTokens = { ...tokens };
      const keys = path.split('.');
      const lastKey = keys.pop();
      const target = keys.reduce((obj, key) => obj[key] = obj[key] || {}, newTokens);
      target[lastKey] = value;
      setTokens(newTokens);
    }
  };

  return (
    <DesignSystemContext.Provider value={value}>
      <div className={cn(
        'unified-design-system',
        `theme-${currentTheme}`,
        `culture-${currentCulture}`,
        reducedMotion && 'reduce-motion',
        highContrast && 'high-contrast'
      )}>
        {children}
      </div>
    </DesignSystemContext.Provider>
  );
};

// Default context value for graceful degradation when used outside provider
const defaultContextValue = {
  tokens: baseTokens,
  currentTheme: 'light',
  currentCulture: 'default',
  setCurrentTheme: () => {},
  setCurrentCulture: () => {},
  componentAPI,
  cn,
  getToken: (path) => {
    return path.split('.').reduce((obj, key) => obj?.[key], baseTokens);
  },
  setCustomToken: () => {}
};

export const useDesignSystem = () => {
  const context = useContext(DesignSystemContext);
  // Return default values instead of throwing - graceful degradation
  if (!context) {
    // Log warning in development only
    if (process.env.NODE_ENV === 'development') {
      console.warn('useDesignSystem called outside DesignSystemProvider - using defaults');
    }
    return defaultContextValue;
  }
  return context;
};

// Component Standardization HOC
export const withDesignSystem = (Component) => {
  return (props) => {
    const { tokens, componentAPI, cn } = useDesignSystem();
    return <Component {...props} tokens={tokens} componentAPI={componentAPI} cn={cn} />;
  };
};

export default DesignSystemProvider;
